import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { mapProfile } from "@/lib/adapt";
import { useCelebrate } from "@/context/MatchCelebration";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, MapPin, Heart, X, ArrowRight } from "lucide-react";

const dayKey = () => `nikah_spotlight_${new Date().toISOString().slice(0, 10)}`;

export function MatchSpotlight() {
  const navigate = useNavigate();
  const celebrate = useCelebrate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(dayKey()) === "1"; } catch { return false; } });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    (async () => {
      try {
        const { data } = await api.get("/browse", { params: { sort: "most_compatible", page: 1, limit: 10 } });
        const p = (data.profiles || []).map(mapProfile).find((x) => !x.hidden);
        if (p) setProfile(p);
      } catch {}
    })();
  }, [dismissed]);

  const dismiss = () => { try { localStorage.setItem(dayKey(), "1"); } catch {} setDismissed(true); };

  const interested = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/actions", { targetId: profile.id, action: "interested" });
      if (data.matched) celebrate(profile);
      else toast.success("Interest sent");
      dismiss();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (dismissed || !profile || user?.is_assistant) return null;
  const location = [profile.city, profile.country].filter(Boolean).join(", ");

  return (
    <div className="relative mb-6 overflow-hidden rounded-3xl border border-amber-300/60 bg-gradient-to-br from-emerald-800 via-emerald-800 to-emerald-950 p-1 shadow-lg animate-fade-up" data-testid="match-spotlight">
      <div className="pattern-bg absolute inset-0 opacity-30" />
      <div className="relative flex flex-col items-stretch gap-4 rounded-[1.35rem] p-4 sm:flex-row sm:items-center sm:p-5">
        <button onClick={dismiss} data-testid="spotlight-dismiss" aria-label="Dismiss" className="absolute right-3 top-3 z-10 rounded-full bg-white/10 p-1.5 text-white/80 hover:bg-white/20"><X className="h-4 w-4" /></button>

        <button onClick={() => navigate(`/profiles/${profile.id}`)} className="relative h-40 w-full shrink-0 overflow-hidden rounded-2xl sm:h-28 sm:w-28" data-testid="spotlight-photo">
          {profile.photo ? <img src={profile.photo} alt={profile.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center bg-emerald-950 text-4xl font-serif text-amber-300/60">{profile.name?.[0]}</div>}
        </button>

        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-950"><Sparkles className="h-3.5 w-3.5" /> Today's Top Match</span>
          <h3 className="mt-2 font-serif text-2xl font-bold text-white">{profile.name}{profile.age ? `, ${profile.age}` : ""}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-emerald-100/85">
            {location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {location}</span>}
            {typeof profile.match_percent === "number" && <span className="flex items-center gap-1 font-semibold text-amber-300"><Sparkles className="h-3.5 w-3.5" /> {profile.match_percent}% compatible</span>}
          </div>
        </div>

        <div className="flex shrink-0 gap-2 sm:flex-col lg:flex-row">
          <button onClick={() => navigate(`/profiles/${profile.id}`)} data-testid="spotlight-view" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20">View <ArrowRight className="h-4 w-4" /></button>
          <button onClick={interested} disabled={busy} data-testid="spotlight-interested" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"><Heart className="h-4 w-4 fill-current" /> Interested</button>
        </div>
      </div>
    </div>
  );
}
